import {
  collection,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  QuerySnapshot,
  DocumentData,
  Unsubscribe,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  ChatMessage,
  ChatDocumentData,
  ReactionDocumentData,
  TypingDocumentData,
  MessageType,
  AVAILABLE_EMOJIS
} from '../models/Chat';
import { WebRTCManager } from './WebRTCManager';

export interface ChatServiceCallbacks {
  onMessagesUpdate: (messages: ChatMessage[]) => void;
  onReactionsUpdate: (reactions: Record<string, number>) => void;
  onUserReactionsUpdate: (userReactions: string[]) => void;
  onTypingUsersUpdate: (typingUsers: string[]) => void;
  onError: (error: Error) => void;
}

export class ChatService {
  private unsubscribeMessages: Unsubscribe | null = null;
  private unsubscribeReactions: Unsubscribe | null = null;
  private unsubscribeTyping: Unsubscribe | null = null;
  private callbacks: ChatServiceCallbacks;
  private webRTCManager: WebRTCManager;

  constructor(callbacks: ChatServiceCallbacks) {
    this.callbacks = callbacks;
    this.webRTCManager = WebRTCManager.shared;
  }

  startListening(pinId: string, currentUserId: string) {
    this.stopListening();

    // Listen to messages
    const messagesCollection = collection(db, 'pins', pinId, 'chat');
    const messagesQuery = query(messagesCollection, orderBy('timestamp', 'asc'));

    this.unsubscribeMessages = onSnapshot(
      messagesQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const messages: ChatMessage[] = snapshot.docs.map(doc => {
            const data = doc.data() as ChatDocumentData;
            return {
              id: doc.id,
              pinId: data.pinId,
              text: data.text,
              senderId: data.senderId,
              senderName: data.senderName,
              timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() :
                        typeof data.timestamp === 'string' ? new Date(data.timestamp) :
                        new Date(data.timestamp),
              messageType: data.messageType || MessageType.TEXT,
              emoji: data.emoji,
              reactions: data.reactions || {},
              userReactions: data.userReactions || []
            };
          });
          this.callbacks.onMessagesUpdate(messages);
        } catch (error) {
          this.callbacks.onError(error as Error);
        }
      },
      (error) => {
        this.callbacks.onError(error);
      }
    );

    // Listen to reactions
    const reactionsCollection = collection(db, 'pins', pinId, 'reactions');

    this.unsubscribeReactions = onSnapshot(
      reactionsCollection,
      async (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const reactions: Record<string, number> = {};
          const userReactions: string[] = [];

          // Count reactions by emoji
          snapshot.docs.forEach(doc => {
            const data = doc.data() as ReactionDocumentData;
            const emoji = data.emoji;
            reactions[emoji] = (reactions[emoji] || 0) + 1;
            if (data.userId === currentUserId) {
              userReactions.push(emoji);
            }
          });

          this.callbacks.onReactionsUpdate(reactions);
          this.callbacks.onUserReactionsUpdate(userReactions);
        } catch (error) {
          this.callbacks.onError(error as Error);
        }
      },
      (error) => {
        this.callbacks.onError(error);
      }
    );

    // Listen to typing indicators — must be 'typing_indicators' to match
    // the iOS app (was 'typing', which also isn't covered by Firestore rules)
    const typingCollection = collection(db, 'pins', pinId, 'typing_indicators');

    this.unsubscribeTyping = onSnapshot(
      typingCollection,
      (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const typingUsers: string[] = [];
          const now = Date.now();

          snapshot.docs.forEach(doc => {
            const data = doc.data() as TypingDocumentData;
            const timestamp = data.timestamp instanceof Timestamp ? data.timestamp.toDate().getTime() : new Date(data.timestamp).getTime();

            // Only show typing if it's recent — iOS shows up to 30s
            if (now - timestamp < 30000) {
              typingUsers.push(data.userName);
            }
          });

          this.callbacks.onTypingUsersUpdate(typingUsers);
        } catch (error) {
          this.callbacks.onError(error as Error);
        }
      },
      (error) => {
        this.callbacks.onError(error);
      }
    );
  }

  stopListening() {
    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
      this.unsubscribeMessages = null;
    }
    if (this.unsubscribeReactions) {
      this.unsubscribeReactions();
      this.unsubscribeReactions = null;
    }
    if (this.unsubscribeTyping) {
      this.unsubscribeTyping();
      this.unsubscribeTyping = null;
    }
  }

  async sendMessage(pinId: string, text: string, senderId: string, senderName: string): Promise<void> {
    try {
      const messagesCollection = collection(db, 'pins', pinId, 'chat');
      await addDoc(messagesCollection, {
        pinId,
        text,
        senderId,
        senderName,
        timestamp: serverTimestamp(),
        messageType: MessageType.TEXT,
        reactions: {},
        userReactions: []
      });
    } catch (error) {
      throw new Error(`Failed to send message: ${error}`);
    }
  }

  // Mirrors iOS sendEmoji — emoji messages appear in chat AND bump the reaction count
  async sendEmoji(pinId: string, emoji: string, senderId: string, senderName: string): Promise<void> {
    if (!AVAILABLE_EMOJIS.includes(emoji)) return;

    await addDoc(collection(db, 'pins', pinId, 'chat'), {
      pinId,
      text: '',
      senderId,
      senderName,
      timestamp: serverTimestamp(),
      messageType: MessageType.EMOJI,
      emoji,
      reactions: {},
      userReactions: []
    });

    // iOS upserts the reaction (repeat taps don't toggle it off)
    await setDoc(doc(db, 'pins', pinId, 'reactions', `${senderId}_${emoji}`), {
      pinId,
      emoji,
      userId: senderId,
      timestamp: serverTimestamp()
    });
  }

  // Rules require the doc id to be "{uid}_{emoji}" — same as iOS EmojiReaction.generateId
  async addReaction(pinId: string, emoji: string, userId: string): Promise<void> {
    try {
      const reactionRef = doc(db, 'pins', pinId, 'reactions', `${userId}_${emoji}`);
      const existing = await getDoc(reactionRef);

      if (existing.exists()) {
        await deleteDoc(reactionRef);
      } else {
        await setDoc(reactionRef, {
          pinId,
          emoji,
          userId,
          timestamp: serverTimestamp()
        });
      }
    } catch (error) {
      throw new Error(`Failed to add reaction: ${error}`);
    }
  }

  async startTyping(pinId: string, userId: string, userName: string): Promise<void> {
    // Rules key typing docs by userId — use setDoc so create and update both work
    const typingDoc = doc(db, 'pins', pinId, 'typing_indicators', userId);
    await setDoc(typingDoc, {
      userId,
      userName,
      timestamp: serverTimestamp()
    });
  }

  async stopTyping(pinId: string, userId: string): Promise<void> {
    try {
      const typingDoc = doc(db, 'pins', pinId, 'typing_indicators', userId);
      await deleteDoc(typingDoc);
    } catch (error) {
      // Document might not exist, that's okay
      console.log('Typing document not found or already deleted');
    }
  }

  destroy() {
    this.stopListening();
  }
}
